import type {
	ComponentType,
	CSSProperties,
	ReactNode,
	Ref,
	SVGProps,
} from "react"
import { IconCaretRightFill18 as CaretRight } from "nucleo-ui-fill-18"

import { cn } from "@productlane/lib/src/browser"

import type { ProjectStatusTypes } from "../icons/project-status-icons"
import { IssueStatusIcon } from "../icons/issue-status-icons"
import { ProjectStatusIcon } from "../icons/project-status-icons"

interface PortalSectionHeaderProps {
	children: ReactNode
	className?: string
	statusType?: "project" | "issue"
	status?: string
	statusColor?: string | null
	Icon?: ComponentType<SVGProps<SVGSVGElement>>
	iconClassName?: string
	count?: number
	collapsible?: boolean
	collapsed?: boolean
	onToggle?: () => void
	mutedIcon?: boolean
	leading?: ReactNode
	iconSlotClassName?: string
	headerRef?: Ref<HTMLElement>
	style?: CSSProperties
}

const STATUS_GRADIENT_COLORS: Record<string, string> = {
	started: "242, 201, 76",
	unstarted: "190, 194, 200",
	planned: "94, 106, 210",
	triage: "245, 158, 11",
	backlog: "190, 194, 200",
	inbox: "190, 194, 200",
	completed: "39, 174, 96",
	canceled: "148, 163, 184",
	paused: "148, 163, 184",
	closed: "148, 163, 184",
	review: "94, 106, 210",
}

function hexToRgbTriplet(hex: string): string | null {
	const cleaned = hex.replace("#", "").trim()
	if (cleaned.length !== 3 && cleaned.length !== 6) return null
	const full =
		cleaned.length === 3
			? cleaned
					.split("")
					.map((c) => c + c)
					.join("")
			: cleaned
	const num = Number.parseInt(full, 16)
	if (Number.isNaN(num)) return null
	const r = (num >> 16) & 255
	const g = (num >> 8) & 255
	const b = num & 255
	return `${r}, ${g}, ${b}`
}

// The status icon carries Linear's own color and nothing else.
// The per-type table used to stand in when the color was missing.
//  An unknown color falls through to the caller's neutral treatment.
export function resolvePortalStatusColor(
	_status?: string | null,
	statusColor?: string | null,
): string | undefined {
	return statusColor ?? undefined
}

function parseRgbTriplet(color: string): [number, number, number] | null {
	const hex = hexToRgbTriplet(color)
	if (hex) {
		const [r, g, b] = hex.split(",").map((c) => Number(c.trim()))
		return [r!, g!, b!]
	}
	const match = color.match(/rgba?\(([^)]+)\)/)
	if (!match) return null
	const [r, g, b] = match[1]!.split(",").map((c) => Number(c.trim()))
	if ([r, g, b].some((c) => c === undefined || Number.isNaN(c))) return null
	return [r!, g!, b!]
}

// True for near-gray colors (low chroma), like Linear's "Planned" #D7D8DB.
export function isNeutralColor(color: string): boolean {
	const rgb = parseRgbTriplet(color)
	if (!rgb) return false
	return Math.max(...rgb) - Math.min(...rgb) < 40
}

export function PortalSectionHeader({
	children,
	className,
	statusType,
	status,
	statusColor,
	Icon,
	iconClassName: iconClassNameProp,
	count,
	collapsible = false,
	collapsed = false,
	onToggle,
	mutedIcon = false,
	leading,
	iconSlotClassName,
	headerRef,
	style,
}: PortalSectionHeaderProps) {
	const rgbTriplet =
		(statusColor ? hexToRgbTriplet(statusColor) : null) ??
		(status ? STATUS_GRADIENT_COLORS[status] : null)

	// Color the status icon with the resolved status color instead of a flat gray.
	const iconColor = mutedIcon
		? undefined
		: resolvePortalStatusColor(status, statusColor)
	const iconClassName = iconColor
		? "h-3.5 w-3.5"
		: mutedIcon
			? "h-3.5 w-3.5 text-gray-400 dark:text-gray-600"
			: "h-3.5 w-3.5 text-gray-450 dark:text-gray-500"
	const iconStyle = iconColor ? { color: iconColor } : undefined

	const gradientStyle: CSSProperties | undefined = rgbTriplet
		? {
				backgroundImage: `linear-gradient(to right, rgba(${rgbTriplet}, 0.04), rgba(${rgbTriplet}, 0) 75%)`,
			}
		: undefined

	const Wrapper = collapsible ? "button" : "div"

	return (
		<Wrapper
			ref={headerRef as Ref<HTMLButtonElement & HTMLDivElement>}
			style={{ ...gradientStyle, ...style }}
			onClick={collapsible ? onToggle : undefined}
			className={cn(
				"sticky top-0 z-20 my-2 flex w-full flex-row items-center bg-gray-100/80 px-3 py-1.5 text-left text-[0.85rem] font-medium backdrop-blur-md max-md:-mx-6 max-md:px-6 md:rounded-lg dark:bg-gray-800/70 dark:md:shadow-sm dark:md:shadow-black",
				className,
			)}
		>
			{leading}
			<div className="relative flex flex-1 items-center gap-3">
				{Icon && (
					<div
						className={cn(
							"flex shrink-0 items-center justify-center",
							iconSlotClassName,
						)}
					>
						<Icon
							className={iconClassNameProp ?? iconClassName}
							style={iconClassNameProp ? undefined : iconStyle}
							strokeWidth={1.75}
						/>
					</div>
				)}
				{!Icon && statusType === "project" && status && (
					<div
						className={cn(
							"flex shrink-0 items-center justify-center",
							iconSlotClassName,
						)}
					>
						<ProjectStatusIcon
							status={status as ProjectStatusTypes}
							customColor={statusColor ?? undefined}
							className={iconClassName}
						/>
					</div>
				)}
				{!Icon && statusType === "issue" && status && (
					<div
						className={cn(
							"flex shrink-0 items-center justify-center",
							iconSlotClassName,
						)}
					>
						<IssueStatusIcon
							status={status}
							color={statusColor ?? undefined}
							className={iconClassName}
						/>
					</div>
				)}
				<div className="flex items-baseline gap-1.5">
					{children}
					{typeof count === "number" && (
						<span className="text-xxs font-normal text-secondary">{count}</span>
					)}
				</div>
			</div>
			{collapsible && (
				<CaretRight
					className={cn(
						"h-[11px] w-[11px] shrink-0 text-tertiary opacity-50 transition-transform duration-[260ms] ease-[cubic-bezier(0.23,1,0.32,1)]",
						!collapsed && "rotate-90",
					)}
				/>
			)}
		</Wrapper>
	)
}
